import { supabase } from "../lib/supabase";

export const authService = {
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    const userId = data.user.id;

    // ✅ Get organization_id from DB
    const { data: membership } = await supabase
      .from("user_org_memberships")
      .select("organization_id")
      .eq("user_id", userId)
      .single();

    if (
      membership?.organization_id &&
      !data.user.user_metadata?.organization_id
    ) {
      await supabase.auth.updateUser({
        data: {
          organization_id: membership.organization_id,
        },
      });

      // Refresh JWT
      const { data: refreshedSession } = await supabase.auth.refreshSession();

      data.session = refreshedSession.session;
      data.user = refreshedSession.session.user;
    }

    const token = data.session.access_token;

    const payload = JSON.parse(atob(token.split(".")[1]));

    const newSessionId = payload.session_id;

    await supabase
      .from("app_user_sessions")
      .update({ is_active: 0 })
      .eq("user_id", userId);

    // Insert new active session
    const { error: insertError } = await supabase
      .from("app_user_sessions")
      .insert({
        user_id: userId,
        session_id: newSessionId,
        device_info:
          typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        is_active: 1,
        last_activity: new Date().toISOString(),
      });

    if (insertError) throw insertError;

    await supabase.auth.signOut({
      scope: "others",
    });

    localStorage.setItem("session_invalidated", Date.now().toString());

    localStorage.removeItem("session_invalidated");

    return data;
  },

  async signUp(email, password, fullName, organizationId) {
    const { data: existingUser, error: lookupError } = await supabase
      .from("user_profiles")
      .select("email")
      .eq("email", email)
      .maybeSingle();

    // if (lookupError) throw lookupError;

    if (existingUser) {
      try {
        const { data: rpcData, error: rpcError } = await supabase.rpc(
          "handle_membership_entry",
          {
            p_email: email,
            p_full_name: fullName,
            p_user_category_id: 3,
            p_org_name: organizationId,
          },
        );

        if (rpcError) {
          throw rpcError;
        }

        // check function response
        if (!rpcData?.success) {
          throw new Error(rpcData?.message || "Something went wrong");
        }

        return rpcData;
      } catch (error) {
        console.error("Membership error:", error.message);
        throw error;
      }
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          organization_id: organizationId,
          org_name: organizationId,
          user_category_id: 3,
        },
      },
    });

    if (error) throw error;
    return data;
  },
  async resendConfirmation(email) {
    const { data, error } = await supabase.auth.resend({
      type: "signup",
      email,
    });
    if (error) throw error;
    return data;
  },
  async signOut() {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session) {
      const token = session.access_token;
      const payload = JSON.parse(atob(token.split(".")[1]));
      const sessionId = payload.session_id;

      await supabase
        .from("app_user_sessions")
        .update({ is_active: 0 })
        .eq("session_id", sessionId);
    }

    await supabase.auth.signOut();
  },

  async getSession() {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  },

  async getCurrentUser() {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  },

  async validateSession() {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return false;

    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return false;

    const token = session.access_token;
    const payload = JSON.parse(atob(token.split(".")[1]));
    const sessionId = payload.session_id;

    const { data } = await supabase
      .from("app_user_sessions")
      .select("is_active")
      .eq("user_id", user.id)
      .eq("session_id", sessionId)
      .maybeSingle();

    if (!data?.is_active) {
      await this.signOut();
      return false;
    }
    return true;
  },

  async getUserProfile(userId, organizationId = null) {
    const { data, error } = await supabase
      .from("user_profiles")
      .select(
        `
  id,
  email,
  full_name,
  avatar_url,
  created_at,
  updated_at,

  user_org_memberships (
    organization_id,
    user_category_id,

    personnel (
      group_id,
      role_id
    )
  )
`,
      )
      .eq("id", userId)
      .single();

    if (error) throw error;

    const memberships = data?.user_org_memberships || [];
    const membership = organizationId
      ? memberships.find((m) => m.organization_id === organizationId) ||
        memberships[0] ||
        {}
      : memberships[0] || {};
    const personnel = membership?.personnel || {};

    return {
      id: data?.id,
      email: data?.email,
      fullName: data?.full_name,
      avatarUrl: data?.avatar_url,

      organization_id: membership?.organization_id || null,
      user_category_id: membership?.user_category_id || null,

      group_id: personnel?.group_id || null,
      role_id: personnel?.role_id || null,

      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
    };
  },

  // ─── Multi-Org Selection ─────────────────────────────────────────────────────

  /**
   * Fetch all active org memberships for a user.
   * Returns: [{ membershipId, orgId, orgName, userCategoryId, categoryName }]
   */
  async getUserMemberships(userId) {
    // Step 1: fetch ALL memberships for this user (no is_active filter at DB level
    // to avoid type-mismatch issues between boolean/integer conventions)
    const { data: memberships, error: memError } = await supabase
      .from("user_org_memberships")
      .select(
        "id, organization_id, user_category_id, is_active, first_bulk_upload",
      )
      .eq("user_id", userId);

    if (memError) {
      console.error("[Auth] getUserMemberships error:", memError);
      throw memError;
    }

    if (!memberships || memberships.length === 0) return [];

    // Client-side filter: is_active = 0 (integer) OR is_active = false (boolean)
    const activeMemberships = memberships.filter(
      (m) => m.is_active === 0 || m.is_active === false || m.is_active === null,
    );

    if (activeMemberships.length === 0) return [];

    // Step 2: batch-fetch org names
    const orgIds = [
      ...new Set(
        activeMemberships.map((m) => m.organization_id).filter(Boolean),
      ),
    ];
    const { data: orgs, error: orgError } = await supabase
      .from("organizations")
      .select("id, name")
      .in("id", orgIds);

    // Step 3: batch-fetch category names (only for active memberships)
    const catIds = [
      ...new Set(activeMemberships.map((m) => m.user_category_id).filter(Boolean)),
    ];
    const { data: categories } = await supabase
      .from("ms_user_category")
      .select("id, category")
      .in("id", catIds);

    const orgMap = Object.fromEntries((orgs ?? []).map((o) => [o.id, o.name]));
    const catMap = Object.fromEntries(
      (categories ?? []).map((c) => [c.id, c.category]),
    );

    return activeMemberships.map((row) => ({
      membershipId: row.id,
      orgId: row.organization_id,
      orgName: orgMap[row.organization_id] ?? "Unknown Organization",
      userCategoryId: row.user_category_id,
      categoryName: catMap[row.user_category_id] ?? "Member",
      firstBulkUpload: row.first_bulk_upload ?? false,
    }));
  },

  /**
   * Fetch allowed routes for a user category from ms_user_cat_menu_map → ms_web_menu.
   * Returns string[] of route paths.
   */
  async getRoutesByCategory(userCategoryId) {
    const { data, error } = await supabase
      .from("ms_user_cat_menu_map")
      .select(`menu_id, ms_web_menu ( id, route, is_active )`)
      .eq("user_category_id", userCategoryId)
      .eq("is_active", 0);
    if (error) {
      console.error("[Auth] getRoutesByCategory error:", error);
      throw error;
    }

    const routes = (data ?? [])
      .filter((row) => row.ms_web_menu?.is_active === 0)
      .map((row) => row.ms_web_menu.route)
      .filter(Boolean);

    return routes;
  },

  async updateProfile(userId, updates) {
    const dbUpdates = {
      updated_at: new Date().toISOString(),
    };
    if (updates?.fullName !== undefined) dbUpdates.full_name = updates.fullName;
    if (updates?.organization !== undefined)
      dbUpdates.organization = updates.organization;
    if (updates?.avatarUrl !== undefined)
      dbUpdates.avatar_url = updates.avatarUrl;

    const { data, error } = await supabase
      .from("user_profiles")
      .update(dbUpdates)
      .eq("id", userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async resetPassword(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) throw error;
  },

  // ─── Forgot Password ────────────────────────────────────────────────────────
  async forgotPassword(email) {
    // Check if email exists in your application
    const { data: user, error: lookupError } = await supabase
      .from("user_profiles")
      .select("id,email")
      .eq("email", email)
      .maybeSingle();

    if (lookupError) throw lookupError;

    if (!user) {
      throw new Error("No account found with this email address.");
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/security/mfa-reset?recovery=true`,
    });

    if (error) throw error;
  },

  // ─── Change Password ────────────────────────────────────────────────────────
  async changePassword(newPassword) {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) throw error;

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      await supabase
        .from("user_profiles")
        .update({
          must_change_password: false,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);
    }

    return data;
  },
};
